import { reaction, runInAction } from "mobx";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import {
  areAuditFiltersEqual,
  AuditFilterStore,
  readAuditFiltersFromSearchParams,
  writeAuditFiltersToSearchParams,
} from "./audit-filter-store";

/**
 * Keeps the audit filter MobX store and namespaced URL params in sync so
 * filters are reactive in the UI and shareable via the address bar.
 */
export const useAuditFilterURLSync = (store: AuditFilterStore) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingFromURLRef = useRef(false);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isUpdatingFromURLRef.current) {
      return;
    }

    const urlFilters = readAuditFiltersFromSearchParams(searchParams);
    if (!areAuditFiltersEqual(urlFilters, store.filters)) {
      runInAction(() => {
        store.applyFromURL(urlFilters);
      });
    }
  }, [searchParams, store]);

  useEffect(() => {
    const disposer = reaction(
      () => store.filters,
      filters => {
        if (isUpdatingFromURLRef.current) {
          return;
        }

        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        updateTimeoutRef.current = setTimeout(() => {
          const urlFilters = readAuditFiltersFromSearchParams(searchParams);
          if (areAuditFiltersEqual(filters, urlFilters)) {
            return;
          }

          isUpdatingFromURLRef.current = true;
          setSearchParams(prev => writeAuditFiltersToSearchParams(prev, filters), {
            replace: true,
          });
          setTimeout(() => {
            isUpdatingFromURLRef.current = false;
          }, 50);
        }, 100);
      },
      { fireImmediately: false },
    );

    return () => {
      disposer();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [searchParams, setSearchParams, store]);

  useEffect(
    () => () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    },
    [],
  );
};
